import { Components, Inputs } from "@thisgorilla/primate-ds";
import { Field, FieldProps, Formik } from "formik";
import { useRef, useState } from "react";
import styled from "styled-components";

import instance from "../../../axios/axios-user";
import { useAppSelector } from "../../../redux";
import {
  addProxyUrlToProfileImage,
  getMentionList,
  RICHTEXT_INITIAL_VALUE,
} from "../../../utils";
import { FlexRow } from "../Styles";
import Footer from "./Footer";
import { PostCommentFieldProps } from "./PostCommentField";
import { findCustomURL } from "./utils";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { CommentAsDropdown } from "./CommentAsDropdown";
import { API_PATHS } from "../../../API/api_paths";

const PostCommentField = ({
  parent_comment_id,
  value = "",
  isEditMode = false,
  setIsEditComment,
  comment_id,
  urls,
  image_id = null,
  post_id,
  onClose = () => {},
  scrollAreaId,
  userName = "",
  comment_profile_picture = "",
}: PostCommentFieldProps.IProps) => {
  const ref = useRef(null);
  const {
    profile_picture,
    suggestedUsers,
    first_name,
    last_name,
    anonymous_persona,
  } = useAppSelector((state) => state.me);
  const [isFocused, setIsFocused] = useState(false);
  const [isFooterShow, setIsFooterShow] = useState(isEditMode ? true : false);
  const [temp_id, setTemp_id] = useState<string | null>(image_id);
  const [urlData, setUrlData] = useState<
    PostCommentFieldProps.Footer["urlData"]
  >(
    urls?.length
      ? {
          id: urls[0].id,
          description: urls[0].description,
          imageUrl: urls[0].image,
          title: urls[0].title,
          url: urls[0].url,
          type: "",
          version: 0,
        }
      : null
  );
  const fieldNameContent = "content";
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const loginUserName = first_name + " " + last_name;
  const [selectedCommentAsOption, setSelectedCommentAsOption] = useState(
    isEditMode ? userName : loginUserName
  );

  useClickOutside({ ref, setState: setDropdownOpen });

  const pictures = {
    [loginUserName]: profile_picture,
    Pod: "/images/common/Gorilla.png",
    "Company Name": "/images/common/Gorilla.png",
    [anonymous_persona]: isEditMode
      ? comment_profile_picture.startsWith("anon")
        ? API_PATHS.global.anonymous_persona + comment_profile_picture
        : addProxyUrlToProfileImage(comment_profile_picture)
      : API_PATHS.global.anonymous_persona + "anon_1.svg",
  };

  return (
    <Formik initialValues={{ content: value }} onSubmit={() => {}}>
      {(formik) => {
        return (
          <FlexRow $gapInPx={10}>
            <Container ref={ref}>
              <CommentAsContainer
                $isFocused={isDropdownOpen}
                onClick={() => {
                  if (isEditMode) return;
                  setDropdownOpen(!isDropdownOpen);
                }}
              >
                <Components.Avatar
                  size="XSmall-28"
                  src={pictures[selectedCommentAsOption]}
                  type="Circle"
                />
                <Components.IconButton
                  iconType={isDropdownOpen ? "ChevronUp" : "ChevronDown"}
                  disabled={false}
                  kind="Tertiary"
                  onClick={() => {}}
                  size="XS"
                />
              </CommentAsContainer>
              {isDropdownOpen && (
                <CommentAsDropdown
                  selectedOption={selectedCommentAsOption}
                  setSelectedOption={setSelectedCommentAsOption}
                />
              )}
            </Container>
            <InputContainer
              $isFocused={isFocused}
              $isFooterShow={isFooterShow}
              onClick={() => {
                setIsFooterShow(true);
              }}
            >
              <Field key={fieldNameContent} name={fieldNameContent}>
                {({ field, form }: FieldProps) => {
                  const { value } = field;
                  const { setFieldValue, validateField } = form;

                  return (
                    <Inputs.RichTextarea
                      fieldName={fieldNameContent}
                      axiosInstance={instance}
                      idPrefix="post"
                      isRequired={false}
                      mentionList={getMentionList(suggestedUsers)}
                      onChangeHandler={(state, html) => {
                        setUrlData(findCustomURL(state.toJSON().root));
                        if (!html || html === RICHTEXT_INITIAL_VALUE) {
                          setFieldValue(fieldNameContent, "");
                        } else {
                          setFieldValue(fieldNameContent, html);
                          setTimeout(() => {
                            validateField(fieldNameContent);
                          }, 0);
                        }
                      }}
                      value={value}
                      placeholder="Add a comment..."
                      variant="Post"
                      showToolbar={false}
                      maxHeight={150}
                      minHeight={24}
                      onFocus={() => {
                        setIsFocused(true);
                      }}
                      onBlur={() => {
                        setIsFocused(false);
                        validateField(fieldNameContent);
                      }}
                      urls={urls}
                      footerComponent={
                        isFooterShow ? (
                          <Footer
                            scrollAreaId={scrollAreaId}
                            formik={formik}
                            urlData={urlData}
                            setTemp_id={setTemp_id}
                            temp_id={temp_id}
                            post_id={post_id}
                            parent_comment_id={parent_comment_id}
                            setIsFooterShow={
                              isEditMode && setIsEditComment
                                ? setIsEditComment
                                : setIsFooterShow
                            }
                            isEditMode={isEditMode}
                            comment_id={comment_id}
                            onClose={onClose}
                            is_anonymous={
                              selectedCommentAsOption === anonymous_persona
                            }
                          />
                        ) : (
                          <></>
                        )
                      }
                      scrollAreaId={scrollAreaId}
                    />
                  );
                }}
              </Field>
            </InputContainer>
          </FlexRow>
        );
      }}
    </Formik>
  );
};

export default PostCommentField;

const InputContainer = styled.div<{
  $isFocused: boolean;
  $isFooterShow: boolean;
}>`
  height: fit-content;
  width: 100%;
  padding: ${({ theme, $isFooterShow }) =>
    $isFooterShow
      ? `${theme.spacing["spacing-2"].value}rem ${theme.spacing["spacing-3"].value}rem`
      : `${theme.spacing["spacing-1"].value}rem ${theme.spacing["spacing-3"].value}rem`};

  border: ${({ theme }) => theme.border_width["border-width-thin"].value}rem
    solid;
  border-radius: 3px;

  ${({ $isFocused }) =>
    $isFocused
      ? Inputs.CommonField.CommonFieldStyle.inputActivecss
      : Inputs.CommonField.getInputCss("Normal")}
`;

const Container = styled.div`
  position: relative;
`;

const CommentAsContainer = styled(FlexRow)<{
  $isFocused: boolean;
}>`
  height: fit-content;
  align-items: center;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing["spacing-05"].value}rem
    ${({ theme }) => theme.spacing["spacing-1"].value}rem;
  border: 1px solid
    ${({ theme }) => theme.Color.border["ui-border-neutral"].value};
  border-radius: ${({ theme }) => theme.radius["radius-xxs"].value}rem;
  ${({ $isFocused }) =>
    $isFocused
      ? Inputs.CommonField.CommonFieldStyle.inputActivecss
      : Inputs.CommonField.getInputCss("Normal")}
`;
